import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function CustomerPayment() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [method, setMethod] = useState('pix');
  const [cardForm, setCardForm] = useState({ number: '', name: '', expiry: '', cvv: '' });
  const [paying, setPaying] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then(r => {
      setOrder(r.data);
      setMethod(r.data.paymentMethod || 'pix');
    }).catch(() => nav('/cliente/home'));
  }, [orderId]);

  // Polling para confirmar PIX
  useEffect(() => {
    if (!polling) return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/orders/${orderId}/payment-status`);
        if (r.data.status === 'paid') {
          clearInterval(iv);
          toast.success('Pagamento confirmado!');
          nav(`/cliente/aguardando/${orderId}`);
        }
      } catch {}
    }, 3000);
    return () => clearInterval(iv);
  }, [polling, orderId, nav]);

  const pay = async () => {
    if ((method === 'card' || method === 'debit') && (!cardForm.number || !cardForm.name || !cardForm.expiry || !cardForm.cvv)) {
      toast.error('Preencha todos os dados do cartão'); return;
    }
    setPaying(true);
    try {
      const body = {};
      if (method === 'debit' || method === 'card') {
        // Tokenização mockada — em produção use o SDK do PagBank no front
        body.cardToken = `tok_${Date.now()}`;
        body.installments = 1;
      }
      const r = await api.post(`/orders/${orderId}/pay`, body);

      if (method === 'pix') {
        if (r.data.pixQrCode) {
          setPixData({ qr: r.data.pixQrCode, code: r.data.pixCode });
          setPolling(true);
          toast.success('PIX gerado! Escaneie o QR Code');
        } else {
          // Fallback sandbox sem QR real
          nav(`/cliente/aguardando/${orderId}`);
        }
        setPaying(false);
        return;
      }

      if (r.data.status === 'waiting_delivery' || r.data.paymentStatus === 'paid') {
        toast.success('Pagamento confirmado!');
        nav(`/cliente/aguardando/${orderId}`);
      } else {
        toast('Aguardando confirmação do pagamento...');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro no pagamento');
    } finally {
      setPaying(false);
    }
  };

  const cashbackLabel = method === 'pix' ? 3 : method === 'debit' ? 1.5 : 0;

  if (!order) return (
    <div className="screen flex items-center justify-center">
      <img src="/logo.png" alt="logo" className="w-16 h-16 object-contain animate-bounce" />
    </div>
  );

  return (
    <div className="screen bg-gray-50 flex flex-col">
      <div className="gradient-hero px-6 pt-10 pb-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="GasNaPorta" className="h-14 w-auto object-contain logo-glow" />
          </div>
          <button onClick={() => nav(-1)} className="text-xl mb-2 block text-sky-200 hover:text-white">←</button>
          <h1 className="text-2xl font-black">Pagamento</h1>
          <p className="text-sky-200 text-sm mt-1">Confirme antes de buscar entregador</p>
        </div>
      </div>

      <div className="flex-1 px-4 py-6 overflow-y-auto fade-in space-y-4">
        {/* Order summary */}
        <div className="bg-white rounded-2xl p-4 shadow-sm card-hover">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">{order.brandEmoji}</span>
            <div>
              <div className="font-bold">{order.brandName} — {order.sizeLabel}</div>
              <div className="text-xs text-gray-500">{order.quantity}x botijão • {order.address}</div>
            </div>
          </div>
          <div className="border-t pt-3 flex justify-between items-center">
            <span className="text-gray-600 font-semibold">Total a pagar</span>
            <span className="text-navy-600 font-black text-xl">R$ {order.customerPrice.toFixed(2)}</span>
          </div>
          {order.cashbackUsed > 0 && (
            <div className="mt-2 text-xs text-green-600 font-semibold">Cashback usado: R$ {order.cashbackUsed.toFixed(2)}</div>
          )}
        </div>

        {/* Method selector */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-3">Forma de pagamento</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { id: 'wallet', label: 'Saldo', icon: '💰' },
              { id: 'pix', label: 'PIX', icon: '⚡' },
              { id: 'debit', label: 'Débito', icon: '💳' },
              { id: 'card', label: 'Crédito', icon: '💳' },
            ].map(m => (
              <button
                key={m.id}
                onClick={() => { setMethod(m.id); setPixData(null); setPolling(false); }}
                className={`py-3 rounded-xl font-bold text-sm border-2 transition-all flex items-center justify-center gap-2 ${
                  method === m.id
                    ? 'border-navy-500 bg-navy-50 text-navy-700'
                    : 'border-gray-200 text-gray-600 hover:border-navy-300'
                }`}
              >
                <span>{m.icon}</span>{m.label}
              </button>
            ))}
          </div>
        </div>

        {/* PIX */}
        {method === 'pix' && (
          <div className="bg-white rounded-2xl p-4 shadow-sm text-center fade-in">
            {pixData ? (
              <div className="space-y-3">
                <div className="text-sm font-bold text-gray-700">Escaneie o QR Code</div>
                <div className="bg-white rounded-xl p-4 inline-block border border-gray-200">
                  <img src={pixData.qr} alt="QR Code PIX" className="w-48 h-48 object-contain" />
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-xs font-mono text-gray-600 break-all">
                  {pixData.code}
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(pixData.code); toast.success('Código copiado!'); }}
                  className="text-xs btn-sky px-4 py-2 rounded-full font-bold"
                >
                  Copiar código PIX
                </button>
                <div className="mt-2 text-xs text-gray-500">
                  Aguardando confirmação...
                  <span className="inline-block w-4 h-4 ml-1 animate-spin">⏳</span>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-gray-600 text-sm">Confirme para gerar o QR Code PIX</div>
                <div className="bg-gray-100 rounded-xl p-6 inline-block">
                  <div className="text-6xl">⚡</div>
                </div>
                <div className="text-xs text-gray-500">
                  O QR Code será gerado pela PagBank com validade de 30 min
                </div>
              </div>
            )}
          </div>
        )}

        {/* Card */}
        {(method === 'card' || method === 'debit') && (
          <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3 fade-in">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Número do Cartão</label>
              <input
                type="text" maxLength={19}
                value={cardForm.number}
                onChange={e => setCardForm(p => ({ ...p, number: e.target.value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim() }))}
                placeholder="0000 0000 0000 0000"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nome no Cartão</label>
              <input
                type="text"
                value={cardForm.name}
                onChange={e => setCardForm(p => ({ ...p, name: e.target.value.toUpperCase() }))}
                placeholder="NOME COMO NO CARTÃO"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Validade</label>
                <input
                  type="text" maxLength={5}
                  value={cardForm.expiry}
                  onChange={e => setCardForm(p => ({ ...p, expiry: e.target.value.replace(/\D/g, '').replace(/^(\d{2})/, '$1/') }))}
                  placeholder="MM/AA"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">CVV</label>
                <input
                  type="text" maxLength={4}
                  value={cardForm.cvv}
                  onChange={e => setCardForm(p => ({ ...p, cvv: e.target.value.replace(/\D/g, '') }))}
                  placeholder="•••"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {method === 'wallet' && (
          <div className="bg-white rounded-2xl p-4 shadow-sm text-center fade-in">
            <div className="text-5xl mb-3">💰</div>
            <div className="font-bold text-gray-800">Pagamento com saldo em carteira</div>
            <div className="text-sm text-gray-500 mt-1">O valor será debitado do saldo adicionado via PIX.</div>
            {!!order.walletUsed && (
              <div className="mt-3 text-xs font-semibold text-emerald-600">Saldo utilizado: R$ {order.walletUsed.toFixed(2)}</div>
            )}
          </div>
        )}

        <div className="bg-navy-50 rounded-2xl p-4 border border-navy-100">
          <div className="text-xs font-bold text-navy-600 uppercase tracking-wide mb-1">Fidelidade</div>
          <div className="text-sm text-gray-700">
            {method === 'wallet'
              ? 'Pagamento com saldo previamente adicionado via PIX.'
              : cashbackLabel > 0
              ? `Pagando com ${method === 'pix' ? 'PIX' : 'débito'}, você recebe R$ ${cashbackLabel.toFixed(2)} de cashback.`
              : 'No crédito você pontua para o bônus de 5 pedidos, mas não recebe cashback imediato.'}
          </div>
        </div>

        {!pixData && (
          <button
            onClick={pay}
            disabled={paying}
            className="w-full py-4 btn-primary rounded-2xl font-bold text-white text-lg"
          >
            {paying ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin">⏳</span> Processando...
              </span>
            ) : `Pagar R$ ${order.customerPrice.toFixed(2)}`}
          </button>
        )}

        <p className="text-center text-xs text-gray-400">
          🔒 Pagamento seguro via PagBank • Após pagar, buscaremos um entregador
        </p>
      </div>
    </div>
  );
}
